import { GLOBAL_PROJECT_TYPE_CHAMBER, GLOBAL_PROJECT_TYPE_PIPE } from "../types";
import SubProject from "../sub-project";
import cuid from "cuid";
import Project from "../project";
import produce from "immer";
import { buildProjectAutomation } from "../../actions/submit-dialog-actions";
import MiniCADAPI_SEARCH from "../MiniCADAPI_SEARCH";

/**
 * Specific shared services for Multibed
 * Functions should be kept clean and pure, avoiding side effects or data mutation
 * @type {{validateOutletOverIsolator: (function(*, *, *): boolean), testSelectedChambersBeforeRemoval: (function(*, *): boolean), validateInternalOutletOnPerimeterRow(*, *, *): boolean, validateIsolatorOverOutlet: (function(*, *, *): boolean), validateSelectedChamberIsActive: (function(*, *): boolean), getActualRows: (function(*, *=, *=, *=, *=, *, *): *), handleManifoldBuildRestrictions: (function(*=, *=): null), removeSelectedChambers: (function(*, *, *): boolean), getChamberCapAccessory: (function(*=, *=, *): ({id: null}|*)), validateInnerFreeSpace: (function(*, *, *, *=, *): boolean), ignoreAccessoryAttachment: (function(*=, *): boolean)}}
 */
const SubProjectService = {

    types: {
        PROJECT_TYPE: {
            chamber: GLOBAL_PROJECT_TYPE_CHAMBER,
            pipe: GLOBAL_PROJECT_TYPE_PIPE,
        },
    },

    /**
     * Prepares viewport to receive the new bed to be loaded.
     * @param props {object} - this.props object
     *
     * @example - setup mapDispatchToProps
     *  {
     *   clearViewPort
     *  };
     *
     * @returns {Promise<void>}
     */
    clearViewPortForMultiBed: async ( props ) => {
        try {
            props.clearViewPort();
        } catch (error) {
            console.error(`SubProjectService.clearViewPortForMultiBed() :: ${error.message}`);
        }
    },

    updateProjectToMultiBed: async ( props ) => {
        try {
            let project = produce(props.project, projectDraft => {
                projectDraft.isMultiBedProject = true;
            });

            let projectInstance = new Project(
                project.id,
                project.projectName,
                project.engineer,
                project.city,
                project.state,
                project.country,
                ( !project.region ? props.region.id : project.region ),
                ( !project.language ? props.language.id : project.language ),
                project.createdDate || new Date(),
                project.statusDate || new Date(),
                project.editedDate || null,
                project.processingStatus,
                project.bomFileUrl,
                project.active,
                project.workItemId,
                project.inputFileURL,
                project.outputFileURL,
                project.projectType,
                project.outputFileURLPipe,
                project.bomFileUrlPipe,
                project.isMultiBedProject,
            );

            await projectInstance.persist(
                props.user.id,
                props.resourcesTokens.projects.tableToken,
            );

            return projectInstance;
        } catch (error) {
            console.error(`SubProjectService.updateProjectToMultiBed() :: ${error.message}`);
        }
    },

    /**
     * Saves or creates an instance of the SubProject
     * @example - use
     *  SubProjectService.saveOrCreateSubProject('ckp2za3nm00003b6p956lcoq8', this.props)
     *     .then((subProjectInstance) => console.log(subProjectInstance))
     *
     * @example - setup mapStateToProps
     *  {
     *   resourcesTokens: state.authentication.resourcesTokens,
     *   user: state.authentication.user,
     *   project: state.project.project,
     *   region: state.localization.region,
     *  };
     *
     * @example - setup mapDispatchToProps
     *  {
     *  getSubProjectStateToPersist,
     *
     *  }
     *
     * @param subProjectId {string | null} - Projects ID, if null sent, then it creates a new subproject
     * @param config {object | null} - configuration object that should contain bedName
     * @param props {object} -  this.props React object
     */
    saveOrCreateSubProject: async ( subProjectId = null, config = null, props ) => {

        let stageScale = props.getStageScale();
        let stagePosition = props.getStagePosition();

        await SubProjectService
            .zoom(1.0, 1.0, 0.0, 0.0, props)
            .catch(error => {
                console.error(`Start SubProjectService.zoom :: ${error.message}`);
            });


        try {
            if (!props.resourcesTokens && !props.resourcesTokens.projects && !props.resourcesTokens.subprojects)
                throw new Error("Now tokens provided by the system");
            if (!props.user) throw new Error("Unable to save without an instance of auth user");
            if (!config) throw new Error("Please provide configuration object");

            const engineer = `${props.user["givenName"]} ${props.user["surname"]}`;
            let subProject = props.project;
            let subProjectInstance, silentState;

            const otherUser = ( config && config.otherUser && config.otherUser.otherUserId ) ? config.otherUser : null;

            if (config && config.subProject && config.silent) {
                // We need to save the oldState on a subproject in order to make a copy of it silently
                const subProjectInstanceSilent = new SubProject(config.subProject.id);

                let oid = props.user.id;
                let blobToken = props.resourcesTokens.projects.blobToken;

                if (otherUser) {
                    oid = otherUser.otherUserId;
                    blobToken = otherUser.otherUserTokens.projects.iBlobToken;
                }
                silentState = await subProjectInstanceSilent
                    .fetchState(
                        oid,
                        blobToken,
                    );
            }

            if (subProjectId) {
                // Dealing with existing Subproject
                // subProject = props.subProject;
                subProjectInstance = new SubProject(subProjectId);
                await subProjectInstance
                    .fetch(
                        props.user.id,
                        props.resourcesTokens["subprojects"]["tableToken"],
                    );

                if (config && config.subProject && config.subProject.id && config.subProject.projectName) {
                    subProjectInstance.id = config.subProject.id;
                    subProjectInstance.projectName = config.subProject.projectName;
                    subProjectInstance.bedName = config.subProject.projectName;
                }

            } else {
                // If we do a save as, we bed to copy the parent id, this will be set on the config object
                // config = {bedName, parentId, otherUser  }
                const parentId = ( config && config.parentId ) ? config.parentId : props.project.id;


                // Creating a new SubProject
                subProjectInstance = new SubProject(
                    cuid(),
                    parentId,
                    config.bedName,
                    subProject.engineer || engineer,
                    subProject.city,
                    subProject.state,
                    subProject.country,
                    props.region.name,
                    subProject.createdDate || new Date(),  // using project data
                    subProject.statusDate || new Date(),
                    subProject.editedDate || null,
                    "status",
                    subProject.boomURL || "",
                    true,
                    subProject.workItemId || "",
                    subProject.inputFileURL || "",
                    subProject.outputFileURL || "",
                    subProject.projectType || "",
                );
            }

            // Config silent should enable to load state from database to save it, if not load from state
            let stateToPersist = config && config.silent ? silentState : props.getSubProjectStateToPersist();

            await subProjectInstance.persistState(
                stateToPersist,
                props.user.id,
                props.resourcesTokens.projects.blobToken,
            );

            await subProjectInstance.persist(
                props.user.id,
                props.resourcesTokens.subprojects.tableToken,
            );


            return subProjectInstance;
        } catch (error) {
            console.error("SubProjectService.saveOrCreateSubProject() :: ", error.message);
        }

        await SubProjectService
            .zoom(
                stageScale.scaleX,
                stageScale.scaleY,
                stagePosition.positionX,
                stagePosition.positionY,
            )
            .catch(error => {
                console.error(`Restore SubProjectService.zoom :: ${error.message}`);
            });

    },

    /**
     * To do a fast State save of the loaded bed, use carefully, because it only will save the loaded state over the provided subProjectId
     * Does not save Subproject data
     * @param subProjectId
     * @param props
     * @returns {Promise<SubProject>}
     */
    simpleCurrentStateSave: async ( subProjectId = null, props ) => {
        try {
            if (!props.resourcesTokens && !props.resourcesTokens.projects && !props.resourcesTokens.subprojects)
                throw new Error("Now tokens provided by the system");
            if (!props.user) throw new Error("Unable to save without an instance of auth user");
            if (!subProjectId) throw new Error("subProjectId is required");

            let subProjectInstance = new SubProject(subProjectId);

            await subProjectInstance
                .fetch(
                    props.user.id,
                    props.resourcesTokens["subprojects"]["tableToken"],
                );

            let stateToPersist = props.getSubProjectStateToPersist();

            await subProjectInstance.persistState(
                stateToPersist,
                props.user.id,
                props.resourcesTokens.projects.blobToken,
            );
            return subProjectInstance;
        } catch (error) {
            console.error(`SubProjectService.simpleCurrentStateSave() :: ${error.message}`);
        }
    },

    /**
     * Sync Automation Process for saving Beds
     * @param subProjectInstance
     * @param props
     * @param isSilent
     * @param subProject
     * @param otherUser
     *
     * @example - setup mapStateToProps
     *  {
     *   projectType: state.globals.projectType,
     *   resourcesTokens: state.authentication.resourcesTokens,
     *   user: state.authentication.user,
     *  };
     *
     * @example - setup mapDispatchToProps
     *  {
     *   buildProjectAutomation,
     *   buildPipeProjectAutomation,
     *   setDirtyStateForMultiBeds,
     *  }
     *
     * @returns {Promise<void>}
     */
    syncAutomationProcess: async ( subProjectInstance, props, isSilent = false, subProject = null, otherUser = {} ) => {
        try {


            console.log("%c start syncAutomationProcess() ", "color:red; background-color:green;");

            // get project automation data
            let automation = ( props.projectType === GLOBAL_PROJECT_TYPE_CHAMBER )
                ? props.buildProjectAutomation()
                : props.buildPipeProjectAutomation();
            // Add the bedName
            automation.bedName = subProjectInstance.projectName;

            let userId, userToken;
            if (otherUser["otherUserId"] === undefined && otherUser["otherUserTokens"] === undefined) {
                userId = props.user.id;
                userToken = props.resourcesTokens.projects.blobToken;
            } else {
                userId = otherUser["otherUserId"];
                userToken = otherUser["otherUserTokens"]["projects"].iBlobToken;
            }

            if (!isSilent) {
                // mimic serialization logic
                automation = JSON.parse(automation.serialize());
            } else {
                // to silently retrieve the automation and reuse it on copy strategy
                // We need to retrieve the old subproject automation file
                automation = await subProject
                    .fetchAutomation(
                        userId,
                        userToken,
                    );
            }
            // persist the state remotely
            await subProjectInstance
                .persistAutomationState(
                    automation,
                    props.user.id,
                    props.resourcesTokens.projects.blobToken,
                );
            //console.log(`syncAutomationProcess() automation.bedName = ${automation.bedName}`)
            //console.log('end syncAutomationProcess()')
        } catch (error) {
            console.error(`SubProjectService.syncAutomationProcess() :: ${error.message}`);
        }
    },

    /**
     * Saves main project
     * @param props {object} -  this.props React object, Requires getStateToPersist()
     * @returns {Promise<*>}
     */
    saveMainProject: async ( props ) => {
        try {
            if (!props) throw new Error("this.props should be provided");
            if (!props.resourcesTokens && !props.resourcesTokens.projects && !props.resourcesTokens.subprojects)
                throw new Error("Now tokens provided by the system");
            if (!props.user) throw new Error("Unable to save without an instance of auth user");

            // if (!props.project) throw new Error('Project is not saved or properly setup');

            let mainProject = props.project;

            let projectName = mainProject.projectName;
            let projectId = mainProject.id;
            let boomFileUrl = mainProject.bomFileUrl;
            let inputFileURL = mainProject.inputFileURL;
            let outputFileURL = mainProject.outputFileURL;
            let processingStatus = mainProject.processingStatus || Project.PROCESSING_STATUS.unsubmitted;

            const engineer = `${props.user["givenName"]} ${props.user["surname"]}`;

            mainProject = produce(mainProject, projectDraft => {
                projectDraft.processingStatus = processingStatus;
                projectDraft.bomFileUrl = boomFileUrl;
            });

            let projectInstance = new Project(
                projectId,
                projectName,
                mainProject.engineer || engineer,
                mainProject.city,
                mainProject.state,
                mainProject.country,
                mainProject.region,
                mainProject.language,
                mainProject.createdDate || new Date(),
                mainProject.statusDate || new Date(),
                mainProject.editedDate || null,
                mainProject.processingStatus,
                mainProject.bomFileUrl,
                true,
                "", //workItemId
                inputFileURL, //inputFileURL
                outputFileURL, //outputFileURL
                props.projectType,
                mainProject.outputFileURLPipe,
                mainProject.bomFileUrlPipe,
                mainProject.isMultiBedProject,
            );

            await projectInstance
                .fetch(
                    props.user.id,
                    props.resourcesTokens["projects"]["tableToken"],
                );

            let stateToPersist = props.getStateToPersist();

            await projectInstance.persistState(
                stateToPersist,
                props.user.id,
                props.resourcesTokens["projects"]["blobToken"],
            );

            await projectInstance.persist(
                props.user.id,
                props.resourcesTokens["projects"]["tableToken"],
            );

            const config = {
                project: projectInstance,
                PartitionKey: props.user.id,
            };

            await MiniCADAPI_SEARCH.mergeOrUploadProjectDocument(config);

            return { projectInstance, message: "saved successfully" };

        } catch (error) {
            console.error("SubProjectService.saveMainProject() :: ", error.message);
        }
    },

    /**
     * Loads subproject by ID
     * @example - use
     *   SubProjectService.loadSubProject('ckp2za3nm00003b6p956lcoq8', this.props)
     *      .then((subProjectInstance) => console.log(subProjectInstance.id));
     *
     * @param subProjectId
     * @param props {object} - this.props object
     *
     * @param loadState
     * @param otherUser
     * @example - setup mapStateToProps
     *  {
     *   resourcesTokens: state.authentication.resourcesTokens,
     *   user: state.authentication.user,
     *  };
     *
     * @example - setup mapDispatchToProps
     *  {
     *  setCurrentSubProject,
     *  clearMainManifoldsState,
     *  restoreSubProjectPersistedState
     *  }
     *
     * @returns {Promise<SubProject> | Promise<*>}
     */
    loadSubProject: async ( subProjectId = null, props, loadState = true, otherUser = null ) => {
        try {
            if (!subProjectId)
                throw new Error("Id for subproject is required");
            if (!props.resourcesTokens && !props.resourcesTokens.projects && !props.resourcesTokens.subprojects)
                throw new Error("No tokens provided by the system");
            if (!props.user) throw new Error("Unable to save without an instance of auth user");

            let subProjectInstance = new SubProject(subProjectId);

            let oid = props.multiBedOtherUserId
                ? props.multiBedOtherUserId
                : props.user.id;
            let token = props.multiBedOtherUserId
                ? props.multiBedOtherUserTokens.subprojects.iTableToken
                : props.resourcesTokens["subprojects"]["tableToken"];

            let blobToken = props.multiBedOtherUserId
                ? props.multiBedOtherUserTokens.projects.iBlobToken
                : props.resourcesTokens["projects"]["blobToken"];

            if (otherUser && otherUser.otherUserId) {
                oid = otherUser.otherUserId;
                token = otherUser.otherUserTokens.subprojects.iTableToken;
                blobToken = otherUser.otherUserTokens.projects.iBlobToken;
            }

            let fetchParams = {
                oid, token, blobToken,
            };

            await subProjectInstance
                .fetch(
                    fetchParams.oid,
                    fetchParams.token,
                );

            if (!subProjectInstance.active) {
                console.error("The subProject was already deleted");
                return;
            }

            props.setCurrentSubProject(subProjectInstance);

            const subProjectState = await subProjectInstance
                .fetchState(
                    fetchParams.oid,
                    fetchParams.blobToken,
                );

            // loadState = false will be used to skip loading the project to memory, and only returning it
            if (loadState) {
                props.clearMainManifoldsState();
                props.restoreSubProjectPersistedState(subProjectState);
                if (subProjectState.globals.projectType === "PIPE") {
                    props.restoreSubProjectPersistedState(subProjectState);
                } else {
                    // Second call need to fix external manifolds
                    // discovered that we need second loads because the manifolds are affected when the component is created
                    props.restoreSubProjectPersistedState(subProjectState);
                }
            }

            return subProjectInstance;
        } catch (error) {
            console.error(`SubProjectService.loadSubProject() :: ${error.message}`);
        }
    },

    loadAllSubProjects: async ( parentId, props ) => {
        try {
            if (!props.resourcesTokens && !props.resourcesTokens.projects && !props.resourcesTokens.subprojects)
                throw new Error("Now tokens provided by the system");

            const subProjects = await SubProject.fetchSubProjects(
                props.user.id,
                parentId,
                props.resourcesTokens["subprojects"]["tableToken"],
            );
            return subProjects;
        } catch (error) {
            console.error(`SubProjectService.loadAllSubProjects() :: ${error.message}`);
            return [];
        }
    },

    loadAllUserSubProjects: async ( userId, iTableToken, props ) => {
        try {
            if (!props.resourcesTokens && !props.resourcesTokens.projects && !props.resourcesTokens.subprojects)
                throw new Error("Now tokens provided by the system");
            const subProjects = await SubProject.fetchUserSubProjects(
                userId,
                iTableToken,
            );
            return subProjects;
        } catch (error) {
            console.error(`SubProjectService.loadAllSubProjects() :: ${error.message}`);
            return [];
        }
    },

    getSubmissionStateForAllSubProjects: async ( props, dispatch, getState ) => {
        let results = [];
        try {
            for (let i = 0; i < props.multiBed.beds.length; i++) {
                const bed = props.multiBed.beds[i];
                const instance = await SubProjectService.loadSubProject(bed.id, props);
                if (instance.projectType === "PIPE") {
                    const {
                        length,
                        width,
                    } = this.props.pipeBed.dimensions;
                    props.setPipeDesign(width.ft, length.ft);
                }
                console.log("BedID:", bed.id, getState());
                const buildResult = buildProjectAutomation()(dispatch, getState);
                results.push(buildResult);
            }
            return results;
        } catch (error) {
            console.error(`SubProjectService.getSubmissionStateForAllSubProjects() :: ${error.message}`);
            return [];
        }
    },

    /**
     * Returns all of the state data associated to all the beds in a multi-bed project
     * @param {string} parentId The parent project's id
     * @param {object} props The current state
     * @returns Array of subprojects state
     */
    loadAllSubProjectsAutomationState: async ( parentId, props ) => {
        try {
            if (!props.authentication.resourcesTokens
                && !props.authentication.resourcesTokens.projects
                && !props.authentication.resourcesTokens.subprojects)
                throw new Error("No tokens provided by the system");

            let subProjectsStateList = [];
            let subProjectInstance;

            for (let i = 0; i < props.multiBed.beds.length; i++) {
                let bed = props.multiBed.beds[i];
                subProjectInstance = new SubProject(bed.id);
                let subProjectState = await subProjectInstance
                    .fetchAutomationState(
                        props.authentication.user.id,
                        props.authentication.resourcesTokens.projects.blobToken,
                    );
                subProjectsStateList.push({ ...subProjectState, bedId: bed.id, bedName: bed.name });
            }

            return subProjectsStateList;
        } catch (error) {
            console.log(`SubProjectService.loadAllSubProjectsAutomationState() :: ${error.message}`);
            return [];
        }
    },

    /**
     * Deletes a sub  project - Deletion is logical only
     * @param subProjectId
     * @param props
     * @returns {Promise<{result: boolean, message: string}>}
     */
    deleteSubProject: async ( subProjectId, props ) => {
        try {
            if (!props.resourcesTokens && !props.resourcesTokens.projects && !props.resourcesTokens.subprojects)
                throw new Error("Now tokens provided by the system");
            if (!props.user) throw new Error("Unable to save without an instance of auth user");
            // if (!props.project) throw new Error('Project is not saved or properly setup');

            let subProjectToDelete = new SubProject(subProjectId);
            await subProjectToDelete
                .fetch(
                    props.user.id,
                    props.resourcesTokens["subprojects"]["tableToken"],
                );
            if (!subProjectToDelete.active) {
                console.error("The subProject was already deleted");
                return;
            }
            subProjectToDelete.active = false;
            await subProjectToDelete.persist(
                props.user.id,
                props.resourcesTokens.subprojects.tableToken,
            );
            return { message: "Record deleted", result: true };
        } catch (error) {
            console.error(`SubProjectService.deleteSubProject() :: ${error.message}`);
            return { message: "An error occurred, could not be deleted", result: false };
        }
    },

    /**
     * Restores a deleted subproject
     * @param subProjectId
     * @param props
     * @returns {Promise<{result: boolean, message: string}>}
     */
    restoreSubProject: async ( subProjectId, props ) => {
        try {
            if (!props.resourcesTokens && !props.resourcesTokens.projects && !props.resourcesTokens.subprojects)
                throw new Error("Now tokens provided by the system");
            if (!props.user) throw new Error("Unable to save without an instance of auth user");
            // if (!props.project) throw new Error('Project is not saved or properly setup');

            let subProjectToDelete = new SubProject(subProjectId);
            await subProjectToDelete
                .fetch(
                    props.user.id,
                    props.resourcesTokens["subprojects"]["tableToken"],
                );
            if (subProjectToDelete.active) {
                console.error("The subProject was already deleted");
                return;
            }
            subProjectToDelete.active = true;
            await subProjectToDelete.persist(
                props.user.id,
                props.resourcesTokens.subprojects.tableToken,
            );
            return { message: "Record deleted", result: true };
        } catch (error) {
            console.error(`SubProjectService.restoreSubProject() :: ${error.message}`);
            return { message: "An error occurred, could not be deleted", result: false };
        }
    },

    fetchAllSubProject: async ( subProjectId, props ) => {
    },

    /**
     * Restores the bed structure to make a new sub project
     * Requires resetAppSettingsState()
     * @param props
     * @returns {Promise<{message: string}>}
     */
    restoreDefaultSettings: async ( props ) => {
        /**
         * testing its use, if debugger is hit, pleased remove this and add a jsdoc.
         * If not remove after August 2021
         */
        console.log("%c <<< renderButton IN USE >>> ", "background: yellow; color: red");
        debugger;
        try {
            props.resetAppSettingsState();
            return { message: "done" };
        } catch (error) {
            console.error("Error on MultiBedService.restoreDefaultSettings() :: ", error.message);
        }
    },

    /**
     * Used to recenter empty beds
     * SubProjectService.setCentralizedCoordinates(this.props);
     * @example mapStateToProps and mapDispatchToProps
     *         computeWidth: state.viewport.computeWidth,
     *         computedHeight: state.viewport.computedHeight,
     *         bedWidth: state.pipeBed.dimensions.width.px,
     *         bedLength: state.pipeBed.dimensions.length.px,
     *         chamberBedHeight: state.chamberbed.height,
     *         chamberBedWidth: state.chamberbed.width,
     *         chamberBedScaleX: state.chamberbed.scaleX,
     *         chamberBedScaleY: state.chamberbed.scaleY,
     *         and
     *         chamberBedSetCentralizedCoordinates,
     *         pipeBedSetCentralizedCoordinates,
     *
     * @param props
     */
    setCentralizedCoordinates: ( props ) => {

        // TODO: Remove all // SubProjectService.setCentralizedCoordinates(this.props);

        setTimeout(() => {
            props.pipeBedSetCentralizedCoordinates({
                x: ( props.computeWidth * 0.5 ) / 2 + ( props.bedWidth ) / 2,
                y: ( props.computedHeight * 1.15 ) / 2 - ( props.bedLength ) / 2,
            });

            props.chamberBedSetCentralizedCoordinates({
                x: props.computeWidth / 2 + ( props.chamberBedHeight * props.chamberBedScaleX ) / 2,
                y: props.computedHeight / 2 - ( props.chamberBedWidth * props.chamberBedScaleY ) / 2 + 400,
            });
        }, 0);
    },

    zoom: async ( scaleX, scaleY, positionX, positionY, props ) => {
        props.scaleStage(scaleX, scaleY);
        props.positionStage(positionX, positionY);
    },
};

export default SubProjectService;



// WEBPACK FOOTER //
// ./src/lib/services/sub-project-service.js